import React, { useState, useEffect } from 'react'
import MuiAccordion from '@material-ui/core/Accordion'
import MuiAccordionSummary from '@material-ui/core/AccordionSummary'
import MuiAccordionDetails from '@material-ui/core/AccordionDetails'
import { Typography, Box, Button, Container, styled } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { TitleH2Bold, TitleMediumLight } from '../../theme/typography'
import CustomButton from '../Button/CustomButton'
import { Plus, Minus } from '../Icon/Icons'
import PropTypes from 'proptypes'
import { withStyles } from '@material-ui/core/styles'
import ReactHtmlParser from 'react-html-parser'

const Accordion = withStyles((theme) => ({
  root: {
    borderTop: (props) => (props.large ? `none` : '1px solid rgba(1, 49, 63, 0.2)'),
    boxShadow: 'none',
    backdropFilter: 'blur(30px)',
    backgroundColor: (props) => (props.bgcolor ? props.bgcolor : theme.colors.white),
    '&:not(:last-child)': {
      borderBottom: 0
    },
    '&:before': {
      display: 'none'
    },
    '&$expanded': {
      margin: 'auto'
    },
    '&:hover': {
      backgroundColor: (props) => (props.large ? theme.colors.cream : 'transparent')
    }
  },
  expanded: {
    backgroundColor: (props) => (props.large ? theme.colors.cream : 'transparent')
  }
}))(MuiAccordion)

const AccordionSummary = withStyles((theme) => ({
  root: {
    borderBottom: (props) =>
      props.large ? `1px solid ${theme.colors.grey3}` : '1px solid  rgba(1, 49, 63, 0.2)',
    marginBottom: (props) => (props.large ? 0 : -1),
    margin: '0 auto',
    maxWidth: (props) => (props.large ? 1264 : 'none'),
    padding: theme.spacing(0, 1, 0, 2.5),
    '& > div': {
      margin: '18px 0',
      [theme.breakpoints.down('sm')]: {
        margin: '8px 0'
      }
    }
  },
  content: {
    '&$expanded': {
      margin: '10px 0 0',
      borderBottom: 'none'
    }
  },
  expanded: {
    '&$root': {
      minHeight: (props) => (props.large ? 82 : 56),
      borderBottom: (props) => (props.large ? 'none' : '1px solid  rgba(1, 49, 63, 0.2)')
    }
  }
}))(MuiAccordionSummary)

const AccordionDetails = withStyles((theme) => ({
  root: {
    display: 'block',
    padding: (props) => (props.large ? theme.spacing(2, 0, 5) : theme.spacing(2)),
    borderBottom: (props) => (props.large ? `1px solid ${theme.colors.grey3}` : 'none'),
    '& p': {
      marginTop: 0,
      [theme.breakpoints.down('sm')]: {
        fontSize: '0.875rem'
      }
    },
    '& a': {
      color: theme.colors.blue1,
      textDecoration: 'none',
      fontWeight: 'bold',
      '&:hover': {
        color: '#67b2ce'
      }
    }
  }
}))(MuiAccordionDetails)

const HelpfulBox = withStyles((theme) => ({
  root: {
    margin: theme.spacing(4, -2, -2, -2),
    padding: theme.spacing(2),
    backgroundColor: theme.colors.grey1,
    justifyContent: 'space-between',
    '& div': {
      alignItems: 'center',
      padding: theme.spacing(0, 1)
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      padding: theme.spacing(1, 2),
      '& div': {
        padding: theme.spacing(1, 0)
      }
    }
  }
}))(Box)

const ButtonBox = withStyles((theme) => ({
  root: {
    margin: -theme.spacing(0.7),
    '& > *': {
      margin: theme.spacing(0.7)
    }
  }
}))(Box)

const ButtonSmall = withStyles((theme) => ({
  root: {
    padding: theme.spacing(0, 1),
    height: theme.spacing(5)
  }
}))(Button)

const ExpandMoreIconLarge = withStyles((theme) => ({
  root: {
    fontSize: '2.2rem',
    color: theme.colors.blue5,
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.8rem'
    }
  }
}))(ExpandMoreIcon)

const FaqContainer = withStyles(() => ({
  root: {
    maxWidth: 1264,
    paddingLeft: 16,
    paddingRight: 16
  }
}))(Container)

const Wrap = styled(Box)(({ theme, bgcolor }) => ({
  position: 'relative',
  backgroundColor: bgcolor ? bgcolor : theme.colors.white
}))

function TextWithAnchorLinks(props) {
  const html = ReactHtmlParser(props.html, {
    transform: (node) => {
      if (node.name === 'a' && node.attribs && node.attribs.href) {
        if (node.attribs.href.match(/^#.*/i)) {
          return (
            <a
              href={node.attribs.href}
              onClick={(e) => {
                e.preventDefault()

                const newUrl = new URL(window.location)
                newUrl.hash = node.attribs.href
                window.history.replaceState({}, '', newUrl)

                props.selectFaq()
              }}
            >
              {node.children[0].data}
            </a>
          )
        }
      }
      return undefined
    }
  })

  return <>{html}</>
}

function FAQList(props) {
  const [expanded, setExpanded] = useState(props.selectedPanel)

  const selectFaq = () => {
    if (window.location.hash && props.list) {
      const tabTitle = decodeURIComponent(window.location.hash.substring(1))
      let selectedTab = props.list.find((tab) => tab.faqId.toLowerCase() === tabTitle)

      if (selectedTab !== undefined && selectedTab.faqId) {
        setExpanded(selectedTab.faqId)
      }
    }
  }

  useEffect(() => {
    window.addEventListener('hashchange', selectFaq, false)
    selectFaq()
    return () => {
      window.removeEventListener('hashchange', selectFaq, false)
    }
  }, [])

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false)
  }

  return (
    <Wrap bgcolor={props.bgcolor}>
      {props.large && props.title ? (
        <Box pb={4} pt={7}>
          <TitleH2Bold variant="h2" gutterBottom align="center">
            {props.title}
          </TitleH2Bold>
        </Box>
      ) : (
        <Typography variant="h3" gutterBottom>
          {props.title}
        </Typography>
      )}

      {props.list &&
        props.list.map((faq, i) => {
          return (
            <div key={`accordion${i}`}>
              <Accordion
                bgcolor={props.bgcolor}
                large={props.large && props.large.toString()}
                key={`panel=${i}`}
                square
                expanded={expanded === (faq.faqId ? faq.faqId : `panel${i}`)}
                onChange={faq.faqId ? handleChange(faq.faqId) : handleChange(`panel${i}`)}
              >
                <AccordionSummary
                  large={props.large && props.large.toString()}
                  expandIcon={
                    expanded === (faq.faqId ? faq.faqId : `panel${i}`) ? (
                      props.large ? (
                        <ExpandMoreIconLarge />
                      ) : (
                        <Minus />
                      )
                    ) : props.large ? (
                      <ExpandMoreIconLarge />
                    ) : (
                      <Plus />
                    )
                  }
                  aria-controls={`panel${i}-content`}
                  id={faq.faqId}
                >
                  {props.large ? (
                    <TitleMediumLight variant="h3">{faq.title}</TitleMediumLight>
                  ) : (
                    <Typography variant="h6">{faq.title}</Typography>
                  )}
                </AccordionSummary>

                <AccordionDetails large={props.large && props.large.toString()}>
                  {props.large ? (
                    <FaqContainer maxWidth="lg">
                      <Typography variant="body1" component="div">
                        <TextWithAnchorLinks html={faq.content} selectFaq={selectFaq} />
                      </Typography>
                    </FaqContainer>
                  ) : (
                    <Typography variant="h5" component="div">
                      <TextWithAnchorLinks html={faq.content} selectFaq={selectFaq} />
                    </Typography>
                  )}
                  {(props.feedbackEnabled || props.contactCta) && (
                    <HelpfulBox display="flex">
                      {props.feedbackEnabled && (
                        <ButtonBox display="flex">
                          <Typography variant="subtitle1">{props.question1}</Typography>
                          <ButtonSmall variant="outlined" href={props.buttonUrlYes}>
                            {props.buttonTextYes}
                          </ButtonSmall>
                          <ButtonSmall variant="outlined" href={props.buttonUrlNo}>
                            {props.buttonTextNo}
                          </ButtonSmall>
                        </ButtonBox>
                      )}
                      <ButtonBox display="flex">
                        <Typography variant="subtitle1">{props.question2}</Typography>
                        {props.contactCta && (
                          <CustomButton
                            color="whiteSmall"
                            variant="contained"
                            href={props.contactCta.href}
                          >
                            {props.contactCta.text}
                          </CustomButton>
                        )}
                      </ButtonBox>
                    </HelpfulBox>
                  )}
                </AccordionDetails>
              </Accordion>
            </div>
          )
        })}
    </Wrap>
  )
}

export default FAQList

FAQList.propTypes = {
  large: PropTypes.bool,
  title: PropTypes.string,
  buttonUrlYes: PropTypes.string,
  buttonUrlNo: PropTypes.string,
  buttonUrlContact: PropTypes.string,
  buttonTextYes: PropTypes.string,
  buttonTextNo: PropTypes.string,
  question1: PropTypes.string,
  question2: PropTypes.string,
  buttonContact: PropTypes.string,
  list: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.string
    })
  )
}
